import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { vehiclesForSale } from '../../mocks/vehicles-for-sale';

export default function VehiclesForSalePage() {
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);

  const handleRequestInfo = (vehicleName: string) => {
    const message = `Hola! Vi el ${vehicleName} en la sección de vehículos en venta de VIP Luxury Cars MKS. Me interesa conocer el precio final, condiciones de compra y disponibilidad. ¿Pueden enviarme más información?`;
    const whatsappUrl = `https://wa.me/522291578562?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-black via-gray-900 to-black">
        <div className="absolute inset-0 bg-[url('https://sunandspeed.com/wp-content/uploads/2025/04/aopsdi.webp')] bg-cover bg-center opacity-20"></div>
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center max-w-4xl mx-auto px-4">
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6">
              <strong>Vehículos</strong> <span className="text-amber-400">en Venta</span>
            </h1>
            <p className="text-xl text-gray-300">
              Selección exclusiva de vehículos premium y de colección
            </p>
          </div>
        </div>
      </section>

      {/* Vehicles Grid */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {vehiclesForSale.map((vehicle) => (
              <article
                key={vehicle.id}
                className="bg-gradient-to-br from-gray-900 to-black rounded-3xl overflow-hidden border border-amber-900/30 hover:border-amber-500/50 transition-all duration-300"
              >
                {/* Image Carousel */}
                <div className="relative h-96">
                  <Swiper
                    modules={[Autoplay, Navigation]}
                    autoplay={{ delay: 4000, disableOnInteraction: false }}
                    navigation
                    loop
                    className="h-full"
                  >
                    {vehicle.images.map((image, index) => (
                      <SwiperSlide key={index}>
                        <img
                          src={image}
                          alt={`${vehicle.brand} ${vehicle.model}`}
                          className="w-full h-full object-cover"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  <div className="absolute top-4 left-4 bg-red-600 text-white px-4 py-2 rounded-full font-bold text-sm z-10">
                    EN VENTA
                  </div>
                </div>

                {/* Vehicle Info */}
                <div className="p-8">
                  <div className="mb-4">
                    <h2 className="text-3xl font-bold text-white mb-2">
                      <strong>{vehicle.brand}</strong>
                    </h2>
                    <p className="text-2xl text-amber-400 mb-2">{vehicle.model}</p>
                    {vehicle.year && (
                      <p className="text-gray-400 text-lg">Año: {vehicle.year}</p>
                    )}
                    {vehicle.mileage && (
                      <p className="text-gray-400 text-lg">Kilometraje: {vehicle.mileage}</p>
                    )}
                  </div>

                  <p className="text-gray-300 mb-6">{vehicle.description}</p>

                  {/* Features */}
                  <div className="mb-6">
                    <h3 className="text-white font-bold mb-3 text-lg">Características:</h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {vehicle.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-gray-400 text-sm">
                          <i className="ri-checkbox-circle-fill text-amber-400 mt-1"></i>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Price */}
                  <div className="mb-6 p-4 bg-amber-500/10 rounded-xl border border-amber-500/30">
                    <p className="text-sm text-gray-400 mb-1">Precio:</p>
                    <p className="text-3xl font-bold text-amber-400">{vehicle.price}</p>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleRequestInfo(`${vehicle.brand} ${vehicle.model}`)}
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-black py-4 rounded-full font-bold text-lg hover:from-amber-600 hover:to-amber-700 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-2xl"></i>
                    SOLICITAR MÁS INFORMACIÓN
                  </button>
                </div>
              </article>
            ))}
          </div>

          {/* Coming Soon Section */}
          <div className="bg-gradient-to-r from-amber-900/20 to-amber-800/20 rounded-3xl p-12 border border-amber-700/30 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-500/20 rounded-full mx-auto mb-6">
                <i className="ri-car-line text-4xl text-amber-400"></i>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Próximamente Más Vehículos
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Pronto, esta plataforma contará con una <strong>selección curada de vehículos premium en venta</strong>, 
                incluyendo algunas de las marcas más prestigiosas del mundo.
              </p>
              <a
                href="https://wa.me/522291578562?text=Hola!%20Visit%C3%A9%20su%20cat%C3%A1logo%20de%20veh%C3%ADculos%20en%20venta%20y%20quiero%20informaci%C3%B3n%20sobre%20los%20modelos%20disponibles.%20%C2%BFPueden%20ayudarme%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 text-white px-8 py-4 rounded-full font-bold hover:bg-green-600 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                Contactar por WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Buy With Us */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl lg:text-5xl font-bold text-center text-white mb-16">
            ¿Por Qué <strong>Comprar con Nosotros</strong>?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-500/10 rounded-full mx-auto mb-6">
                <i className="ri-shield-check-line text-4xl text-amber-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                <strong>Vehículos Verificados</strong>
              </h3>
              <p className="text-gray-400">
                Todos nuestros vehículos pasan por una <strong>inspección rigurosa</strong> y cuentan con documentación completa
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-500/10 rounded-full mx-auto mb-6">
                <i className="ri-file-list-3-line text-4xl text-amber-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                <strong>Historial Completo</strong>
              </h3>
              <p className="text-gray-400">
                Proporcionamos el <strong>historial completo de mantenimiento</strong> y servicios de cada vehículo
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-500/10 rounded-full mx-auto mb-6">
                <i className="ri-customer-service-2-line text-4xl text-amber-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                <strong>Asesoría Personalizada</strong>
              </h3>
              <p className="text-gray-400">
                Nuestro equipo te brinda <strong>asesoría experta</strong> durante todo el proceso de compra
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            ¿Interesado en Algún Vehículo?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Contáctanos por WhatsApp para obtener más información, agendar una visita o realizar una prueba de manejo
          </p>
          <a
            href="https://wa.me/522291578562?text=Hola!%20Estoy%20interesado%20en%20adquirir%20uno%20de%20sus%20veh%C3%ADculos%20premium%20en%20venta.%20%C2%BFPueden%20enviarme%20el%20cat%C3%A1logo%20actualizado%20con%20precios%3F"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-green-500 text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-green-600 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-3xl"></i>
            Contactar Ahora
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
